import type { IndustryPage } from "@/lib/data";

export const mainContractors: IndustryPage = {
  slug: "main-contractors",
  seoTitle:
    "Contractor Website Design UK | Tender-Ready, £1,000",
  seoDescription:
    "Contractor website design for UK main contractors: case studies, accreditations, H&S and framework pages. Fixed £1,000, live in 7 days. Book a free call.",
  serviceName: "Website design for main contractors",
  tldr:
    "ConstructionWebDesign.uk offers contractor website design for UK main contractors: sector case studies, capability statements, the accreditations you hold and a tender enquiry form. The Website package is £1,000, a one-off fixed price with no subscription, or £1,399 with logo and branding. The site is live in 7 days from the discovery call, and you own the website.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Contractor website design · UK",
    headline: "Website design for main contractors, built for tenders and frameworks.",
    highlight: "built for tenders and frameworks",
    sub: "Websites for UK main contractors with sector case studies, a capability statement download, the accreditations you hold, a health and safety page and a tender and framework enquiry form.",
    secondaryCta: { label: "See contractor demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/hero/bricklaying_masonry_slider_ratio.jpg",
      alt: "Main contractor's site team coordinating a multi-trade commercial build in progress",
      headlineTop: "Win bigger commercial",
      headlineBottom: "contracts, faster.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/brentor.jpg",
    },
  ],
  demoCategory: "General Contractor",
  demosHeading: "Contractor website demo templates.",
  demosSub:
    "Demo templates for main contractors and commercial build firms. Open any of them to see the layout, copy and feel a commercial buyer would see. These are demo templates, not client websites.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a main contractor need a website?",
    intro:
      "A main contractor needs a website because clients, consultants and procurement teams often check it when they put a tender list together. A contractor website shows your sectors, delivered projects, accreditations and safety record in one place, before anyone picks up the phone.",
    points: [
      {
        title: "Show the sectors and scale you work at.",
        body: "A clear contractor website tells a buyer which sectors you build in, the size of job you take on and the regions you cover. Without that, a procurement team has to guess whether your firm fits the package.",
        icon: "Buildings",
      },
      {
        title: "Let delivered projects make the case.",
        body: "Case-study pages with photos, scope, sector and programme give a client evidence they can check for themselves. Each project page also gives Google another page it can show for sector searches.",
        icon: "Sparkle",
      },
      {
        title: "Make the accreditation check quick.",
        body: "Constructionline, CHAS, SafeContractor, ISO 9001 and ISO 14001: the accreditations you hold sit near the top of the page, linked to the official registers, so a pre-qualification reviewer can check them quickly.",
        icon: "Shield",
      },
      {
        title: "Turn a visit into a tender enquiry.",
        body: "A capability statement download, a tender and framework enquiry form and a named contact give a buyer a clear next step, instead of a generic contact page.",
        icon: "Mail",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What should a contractor website include?",
    intro:
      "A contractor website should include sector case studies, a capability statement download, the accreditations you hold, a health and safety page and a tender and framework enquiry form. ConstructionWebDesign.uk plans each one into the sitemap before design starts, so every page has a job to do.",
    items: [
      {
        title: "Project case-study pages by sector.",
        body: "One page per delivered project: client type, sector, scope, contract form (for example JCT Design and Build), programme, value band if you choose to share it, and photography. Pages are grouped by sector, such as education, healthcare, retail, industrial and public sector, so a buyer can find comparable work quickly.",
      },
      {
        title: "Capability statement download.",
        body: "Your capability statement as a branded PDF, covering sectors, key projects, accreditations and team, ready to send alongside a PAS 91 pre-qualification questionnaire or a public sector selection questionnaire. The download can sit behind a short email form so you know who has taken a copy.",
      },
      {
        title: "Accreditation strip near the top of the page.",
        body: "The accreditations you hold, such as Constructionline, CHAS, SafeContractor, ISO 9001, ISO 14001, ISO 45001, FORS, NHBC or the Considerate Constructors Scheme, shown as badges with registration numbers linked to the official register. Anything your firm does not hold stays off the page.",
      },
      {
        title: "Health and safety page.",
        body: "Your health and safety policy, how you manage CDM 2015 duties when acting as principal contractor, site inductions and CSCS card checks, and the SSIP accreditations you hold, such as CHAS or SafeContractor. If you choose to publish your accident frequency rate, it sits here too.",
      },
      {
        title: "Sector landing pages.",
        body: "Separate pages for education, healthcare, retail, industrial, residential and public sector work, each written for the buyer in that sector and each a separate page Google can show for sector searches.",
      },
      {
        title: "Tender and framework enquiry form.",
        body: "Not a generic contact form. The tender enquiry form asks for sector, project value, procurement route or framework, and decision date, and it can send each enquiry to the person in your team who handles that work.",
      },
      {
        title: "Search foundations for a commercial contractor.",
        body: "On-page SEO for the sectors and regions you work in, schema markup describing your firm, and a sitemap that makes it clear to Google you are a commercial contractor rather than a domestic trade.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a contractor website cost in the UK?",
      a: "A contractor website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription, or £1,399 with logo and branding. Typical UK ranges for comparison: a freelancer charges about £4,000–£5,000 (billed £50–£120 an hour) and an agency £5,000–£25,000 over 8–12 weeks. The main contractor owns the website once it is live.",
    },
    {
      q: "How long does it take to build a contractor website?",
      a: "A contractor website from ConstructionWebDesign.uk is live in 7 days from the discovery call. The process runs in five steps: discovery call, strategy and sitemap, design, build, then launch and support. It helps to have project photos, accreditation certificates and your capability statement ready before the call.",
    },
    {
      q: "What should a main contractor website include?",
      a: "A main contractor website should include project case studies grouped by sector, a capability statement download, the accreditations you hold (such as Constructionline, CHAS, SafeContractor, ISO 9001 and ISO 14001), a health and safety page, team profiles and a tender and framework enquiry form. ConstructionWebDesign.uk plans these pages into the sitemap before design starts, so each one has a clear job.",
    },
    {
      q: "Do main contractors need a website?",
      a: "Yes, a main contractor needs a website because clients, consultants and procurement teams often look a firm up before adding it to a tender list. A contractor website gives them your sectors, delivered projects, accreditations and safety information in one place, and a direct route to send a tender or framework enquiry. Without one, a buyer has little to judge you on beyond the PQQ.",
    },
    {
      q: "How do contractor websites get found on Google?",
      a: "Contractor websites get found on Google through pages that match what buyers search for: one page per sector, one page per delivered project and clear details of the regions you work in. A contractor website from ConstructionWebDesign.uk includes on-page local SEO, schema markup describing the firm and a sitemap set up for a commercial contractor. No one can promise rankings, but a clear page structure gives Google the right pages to show.",
    },
    {
      q: "How is general contractor website design different from a builder website?",
      a: "General contractor website design is aimed at clients, consultants and procurement teams, while a builder website is aimed mainly at homeowners. A builder website leads with local jobs, reviews and a quote form. A main contractor website leads with sectors, case studies, a capability statement, accreditations and tender enquiries. ConstructionWebDesign.uk plans the sitemap for a main contractor around how commercial buyers shortlist firms.",
    },
    {
      q: "Should CHAS and Constructionline badges go on a contractor website?",
      a: "Yes, the accreditations a main contractor holds, such as Constructionline, CHAS, SafeContractor, ISO 9001 and ISO 14001, should be shown near the top of a contractor website. ConstructionWebDesign.uk adds them as badges with registration numbers linked to the official register, so a pre-qualification reviewer can check them quickly. Accreditations the firm does not hold are left off.",
    },
    {
      q: "Can a contractor website handle tender and framework enquiries?",
      a: "Yes, a contractor website from ConstructionWebDesign.uk can include a tender and framework enquiry form and a capability statement download. The form asks for sector, project value, procurement route or framework and decision date, and it can send each enquiry to the right person in your team. The capability statement is a branded PDF that can sit behind a short email form.",
    },
  ],
  guides: [
    { href: "/blog/how-much-should-a-construction-website-cost", label: "How much does a construction website cost in the UK?" },
    { href: "/blog/how-to-win-subcontractor-work", label: "How to win subcontractor work and get on tender lists" },
    { href: "/blog/chas-accreditation-explained", label: "CHAS accreditation explained: cost, process, worth it?" },
    { href: "/blog/online-presence-construction-company", label: "Online presence for construction companies: a guide" },
  ],
  finalCta: {
    headline: "Plan your contractor website on a free call.",
    highlight: "contractor website",
    sub: "Book a free 30-minute call about your sectors, projects and accreditations. The price is a fixed £1,000, and the site is live in 7 days.",
  },
};
